
import React from 'react'
import * as translator from "../../../../utils/translator";


interface ThankYouStepProps {
    stepNumber: number;
    language: string;
    page: string;
    isOrderOnlyPrescription?: boolean;
    isERecipe?: boolean;
    isEPrescriptionOnly: boolean;
    isSelfPickUp?: boolean;
    isRxSelfPickUp?: boolean;
    selfPickupPaymentModel?: string;
    isCannaleoPreSelected?: boolean;
    isSchengen?: boolean;
    placeHoldersMap: Record<string, string>;
    isOtcOnly?: boolean;
    isTestLab?: boolean;
    isExpressShipping?: boolean;
    isVideoConsultation?: boolean;
    country?: string;
    isPreOrder?: boolean;
    isNextDayDisplayed?: boolean;
}


const applyPlaceholders = (str: string, placeholdersToValues: Record<string, string>) => {
    let result = str;
    for (const [from, to] of Object.entries(placeholdersToValues)) {
        result = result.replace(new RegExp(from, 'g'), to); // replace all occurrences
    }
    return result;
};


const ThankYouStep: React.FC<ThankYouStepProps> = ({ placeHoldersMap, isEPrescriptionOnly, isSchengen = false,isCannaleoPreSelected, stepNumber, language, page, isOrderOnlyPrescription, isERecipe, isSelfPickUp = false, isRxSelfPickUp = false, selfPickupPaymentModel = 'prescription_and_medication', isExpressShipping=false, isVideoConsultation = false, isOtcOnly, isTestLab = false, country, isPreOrder = false, isNextDayDisplayed = false }) => {

    const getRxSelfPickupSuffix = () =>
        selfPickupPaymentModel === 'prescription_only' ? '_rx_self_pickup_prescription_only' : '_rx_self_pickup_pay_once';

    const getTitle = () => {
        if (isPreOrder && stepNumber === 3) {
            return translator.translate(language, page, "page/step3_title_pre_order")
        } else if (isSchengen) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_schengen")
        } else if (isRxSelfPickUp && stepNumber >= 2) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title" + getRxSelfPickupSuffix())
        } else if (isSelfPickUp) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_self_pickup")
        } else if (isCannaleoPreSelected) {
            return applyPlaceholders(translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_cannaleo_pre_selected"), placeHoldersMap)
        } else if (isEPrescriptionOnly) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_e_prescription_only")
        } else if (isOrderOnlyPrescription) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_only_prescription")
        } else if (isERecipe) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_erecipe")
        } else if (isExpressShipping) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_express_delivery")
        } else if (isTestLab) {
            // before isOtcOnly: test-lab orders are OTC-only too
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_test_lab")
        } else if (isOtcOnly) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_otc_only")
        } else if (isVideoConsultation && (language !== 'fr' || ![2, 3].includes(stepNumber))) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title_video_consultation")
        } else if (isNextDayDisplayed && stepNumber === 3) {
            return translator.translate(language, page, "page/step3_title_next_day")
        } else {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_title")
        }
    }

    const getContent = () => {
        const useChDeliveryText = country?.toLowerCase() === "ch";
        if (isSchengen) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_schengen")
        } else if (isRxSelfPickUp && stepNumber >= 2) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content" + getRxSelfPickupSuffix())
        } else if (isSelfPickUp) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_self_pickup")
        } else if (isCannaleoPreSelected) {
            return applyPlaceholders(translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_cannaleo_pre_selected"), placeHoldersMap)
        } else if (isEPrescriptionOnly) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_e_prescription_only")
        } else if (isOrderOnlyPrescription) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_only_prescription")
        } else if (isERecipe) {
            const suffix = useChDeliveryText ? "_ch" : "";
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_erecipe" + suffix)
        } else if (isExpressShipping) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_express_delivery")
        } else if (isTestLab) {
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_test_lab")
        } else if (isOtcOnly) {
            const suffix = useChDeliveryText ? "_ch" : "";
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_otc_only" + suffix)
        } else if (isVideoConsultation && (language !== 'fr' || ![3].includes(stepNumber))) {
            const suffix = useChDeliveryText ? "_ch" : "";
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content_video_consultation" + suffix)
        } else {
            const suffix = useChDeliveryText && stepNumber === 3 ? "_ch" : "";
            return translator.translate(language, page, "page/step" + stepNumber.toString() + "_content" + suffix)
        }
    }


    return (
        <div key={stepNumber} className="thankyou-step">

            <div className="thankyou-step-circle">
                {stepNumber}
            </div>

            <p className="thankyou-step-title">
                { getTitle() }
            </p>

            <p className="thankyou-step-content">
                { getContent() }
            </p>
        </div>
    )
}

export default ThankYouStep
